import { authedFetch } from "@/lib/authed-fetch";
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2, Fingerprint } from 'lucide-react';
import { startAuthentication } from '@simplewebauthn/browser';
import { setAuthUser, setAccessTokenExpiry } from '@/lib/auth';
import { useAuth } from '@/contexts/auth-context';
import acoloriLogo from '@assets/Acolori-Logo_1767006412108.png';

interface LoginResponse {
  accessTokenExpiresInMs?: number;
  user: {
    id: number;
    email: string;
    username: string;
    role: string;
    firstName?: string;
    lastName?: string;
  };
}

// Adopt credentials the user may already have typed into the static
// pre-mount login shell in index.html (captured by main.tsx just before
// React mounted). Read lazily at component init — NOT at module scope,
// where it would run before main.tsx assigns it — and clear the global
// immediately so the password does not linger on window.
function adoptBootLogin(): { email: string; password: string; focus: string | null } {
  const boot = (window as any).__bootLogin;
  delete (window as any).__bootLogin;
  return {
    email: boot?.email ?? '',
    password: boot?.password ?? '',
    focus: boot?.focus ?? null,
  };
}

export default function LoginPage() {
  const [, setLocation] = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  // A stale/expired session can land the user here via a hard redirect while
  // the auth context quietly refreshes the session in the background. Without
  // this guard the login form and globally-mounted authenticated UI (chat
  // dock, overlays) render at the same time. If the session turns out to be
  // valid, leave the login page immediately.
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      setLocation('/');
    }
  }, [authLoading, isAuthenticated, setLocation]);
  const [boot] = useState(adoptBootLogin);
  const [email, setEmail] = useState(boot.email);
  const [password, setPassword] = useState(boot.password);

  // Put the cursor back where the user was typing in the pre-mount shell.
  useEffect(() => {
    if (!boot.focus) return;
    const el = document.getElementById(boot.focus) as HTMLInputElement | null;
    if (el) {
      el.focus();
      // setSelectionRange throws on type="email" inputs — best effort only.
      try { el.setSelectionRange(el.value.length, el.value.length); } catch {}
    }
  }, [boot.focus]);
  const [error, setError] = useState('');
  const [passkeyLoading, setPasskeyLoading] = useState(false);

  const handleLoginSuccess = (data: LoginResponse) => {
    // Tokens are set as httpOnly cookies by the server — never stored here.
    setAuthUser(data.user as any);
    setAccessTokenExpiry(data.accessTokenExpiresInMs);
    setLocation('/');
    window.location.reload();
  };

  const loginMutation = useMutation({
    mutationFn: async () => {
      const response = await authedFetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.status === 429) {
        throw new Error('Too many login attempts. Please wait 30 seconds and try again.');
      }

      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        data = { error: text || response.statusText };
      }

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      return data as LoginResponse;
    },
    onSuccess: handleLoginSuccess,
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  const handlePasskeyLogin = async () => {
    setError('');
    setPasskeyLoading(true);
    try {
      const optionsRes = await authedFetch('/api/passkeys/login-options', { method: 'POST' });
      if (!optionsRes.ok) {
        throw new Error('Failed to get passkey options');
      }
      const optionsData = await optionsRes.json();
      const { challengeToken, ...optionsJSON } = optionsData;

      const authResp = await startAuthentication({ optionsJSON });

      const verifyRes = await authedFetch('/api/passkeys/login-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: authResp, challengeToken }),
      });

      if (!verifyRes.ok) {
        const errData = await verifyRes.json().catch(() => ({}));
        throw new Error(errData.error || 'Passkey login failed');
      }

      const data: LoginResponse = await verifyRes.json();
      handleLoginSuccess(data);
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled');
      } else {
        setError(err?.message || 'Passkey login failed');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    loginMutation.mutate();
  };

  return (
    <div className="relative min-h-screen bg-background flex flex-col items-center p-4">
      <div className="login-glow" aria-hidden />
      {/* Top spacer: logo sits exactly centered between the browser's top
          edge and the top of the login card. */}
      <div className="flex-1 flex items-center min-h-0">
        <img className="login-brand" src={acoloriLogo} alt="Acolori" draggable={false} />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 max-sm:hidden">
          <CardTitle className="text-2xl font-bold text-center">
            Project Management System
          </CardTitle>
          <CardDescription className="text-center">
            Sign in to your account to continue
          </CardDescription>
        </CardHeader>
        <CardContent className="max-sm:pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                disabled={loginMutation.isPending}
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={loginMutation.isPending}
                data-testid="input-password"
              />
            </div>
            
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loginMutation.isPending || passkeyLoading}
              data-testid="button-login"
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-600" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handlePasskeyLogin}
              disabled={loginMutation.isPending || passkeyLoading}
              data-testid="button-passkey-login"
            >
              {passkeyLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Fingerprint className="mr-2 h-4 w-4" />
              )}
              Sign in with Passkey
            </Button>
          </form>
        </CardContent>
      </Card>
      {/* Bottom spacer keeps the card vertically centered overall. */}
      <div className="flex-1 min-h-0" aria-hidden />
    </div>
  );
}
